import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Trash2 } from '../../icons';
import { tMemo, getMemoLang, subscribeMemoLang } from '../../lib/memo-i18n';
import { MemoFindBar, MemoFindHighlight } from './MemoFindBar';
import { findMatches, scrollToOffset } from './memo-find';
import type { MemoDraft, SaveState } from '../../store/memoStore';
import type { MemoDto } from '../../api/memos';

interface MemoEditorProps {
  draft: MemoDraft;
  saveState: SaveState;
  savedAt: string | null;
  /** 409 충돌 시 서버 최신본 — 있으면 병합 배너를 띄운다(§6 / D-3). */
  conflict: MemoDto | null;
  onChange: (patch: { title?: string; body?: string }) => void;
  onResolveConflict: (mode: 'overwrite' | 'takeServer') => void;
  /** Ctrl/Cmd+Enter — 저장 후 닫기. */
  onSaveAndClose: () => void;
  /**
   * 열어둔 메모 삭제. 미전달 시 버튼 미렌더(기존 호출부 무영향).
   * 종전에는 삭제 진입점이 목록 카드의 hover 아이콘뿐이라, 메모를 연 상태에서 지우려면
   * 목록으로 되돌아가야 했다. 편집 중에도 바로 지울 수 있도록 하단에 상시 노출한다.
   */
  onDelete?: () => void;
}

function hhmm(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * MemoEditor — 편집 뷰 (design-20260724 §4.2 / §4.7).
 * 저장은 부모(memoStore)의 debounce 자동 저장이 담당하고, 여기서는 입력과 상태 표시만 한다.
 */
export function MemoEditor({
  draft, saveState, savedAt, conflict, onChange, onResolveConflict, onSaveAndClose, onDelete,
}: MemoEditorProps) {
  useSyncExternalStore(subscribeMemoLang, getMemoLang, getMemoLang);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  // 진입 시 본문 끝에 포커스(§8 접근성) — 바로 이어 쓸 수 있게.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
    // draft.id 가 바뀔 때(다른 메모 열기)만 다시 포커스.
  }, [draft.id]);

  // ── 본문 내 찾기 (2026-08-18) ───────────────────────────────────────────
  // `jump` 는 "이동해 달라" 는 신호다. 본문을 타이핑할 때도 matches 는 다시 계산되지만,
  // 그때 따라 스크롤하면 쓰던 자리가 튄다 — 찾기 조작(검색어 변경·다음·이전)일 때만 올린다.
  const findInputRef = useRef<HTMLInputElement>(null);
  const [find, setFind] = useState({ q: '', i: 0, jump: 0 });
  const matches = useMemo(() => findMatches(draft.body, find.q), [draft.body, find.q]);
  const total = matches.length;
  const cur = total > 0 ? Math.min(find.i, total - 1) : 0;

  // 다른 메모를 열면 찾기 상태를 비운다(이전 메모의 검색어가 남아 있으면 혼란).
  useEffect(() => { setFind({ q: '', i: 0, jump: 0 }); }, [draft.id]);

  useEffect(() => {
    if (find.jump === 0) return;
    const ta = bodyRef.current;
    const m = matches[cur];
    if (!ta || !m) return;
    scrollToOffset(ta, m.start);
    // 선택도 함께 남긴다 — 본문을 클릭해 들어오는 순간 그 자리에서 이어 쓸 수 있다.
    try { ta.setSelectionRange(m.start, m.end); } catch { /* 비활성 등 — 이동만으로 충분 */ }
    // matches/cur 는 jump 시점의 값을 쓰면 되고, 본문 타이핑으로 인한 재계산은 무시한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [find.jump]);

  // Ctrl/Cmd+F — 메모 패널 안에 포커스가 있을 때만 가로챈다(그 밖에서는 브라우저 찾기 유지).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F'))) return;
      const input = findInputRef.current;
      const panel = input?.closest('.memo-panel');
      if (!panel || !panel.contains(document.activeElement)) return;
      e.preventDefault();
      input?.focus();
      input?.select();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <>
      <MemoFindBar
        query={find.q}
        index={cur}
        total={total}
        inputRef={findInputRef}
        onQueryChange={(q) => setFind((f) => ({ q, i: 0, jump: f.jump + 1 }))}
        onNext={() => setFind((f) => ({ ...f, i: total > 0 ? (cur + 1) % total : 0, jump: f.jump + 1 }))}
        onPrev={() => setFind((f) => ({ ...f, i: total > 0 ? (cur - 1 + total) % total : 0, jump: f.jump + 1 }))}
        onClear={() => setFind((f) => ({ q: '', i: 0, jump: f.jump }))}
      />

      {conflict && (
        <div className="memo-conflict" role="alert">
          <span className="memo-conflict-title">{tMemo('memo.conflict_title')}</span>
          <span className="memo-conflict-desc">{tMemo('memo.conflict_desc')}</span>
          <span className="memo-conflict-acts">
            <button type="button" className="memo-conflict-warn" onClick={() => onResolveConflict('overwrite')}>
              {tMemo('memo.conflict_overwrite')}
            </button>
            <button type="button" onClick={() => onResolveConflict('takeServer')}>
              {tMemo('memo.conflict_take_server')}
            </button>
          </span>
        </div>
      )}

      <input
        className="memo-ed-title"
        value={draft.title}
        onChange={(e) => onChange({ title: e.target.value })}
        placeholder={tMemo('memo.title_placeholder')}
        aria-label={tMemo('memo.title_placeholder')}
        maxLength={200}
      />

      {/* 본문 상자 — 하이라이트 오버레이를 본문 뒤에 겹치기 위한 자리(기존 textarea 속성 무변경). */}
      <div className="memo-ed-bodywrap">
        <MemoFindHighlight body={draft.body} matches={matches} current={cur} textareaRef={bodyRef} />
        <textarea
          ref={bodyRef}
          className="memo-ed-body"
          value={draft.body}
          onChange={(e) => onChange({ body: e.target.value })}
          placeholder={tMemo('memo.body_placeholder')}
          aria-label={tMemo('memo.body_placeholder')}
          maxLength={20000}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
              e.preventDefault();
              onSaveAndClose();
            }
          }}
        />
      </div>

      <div className="memo-foot">
        {/* 아직 서버에 저장되지 않은 빈 메모(draft.id === null)는 지울 대상이 없다. */}
        {onDelete && draft.id !== null && (
          <button
            type="button"
            className="memo-ed-del"
            onClick={onDelete}
            title={tMemo('memo.delete')}
            aria-label={tMemo('memo.delete')}
          >
            <Trash2 size={13} />
            {tMemo('memo.delete')}
          </button>
        )}
        <span
          className={`memo-save memo-save--${saveState}`}
          aria-live="polite"
        >
          <span className="memo-save-dot" aria-hidden />
          {saveState === 'saving' && tMemo('memo.saving')}
          {saveState === 'saved' && tMemo('memo.saved', { time: hhmm(savedAt) })}
          {saveState === 'failed' && tMemo('memo.save_failed')}
        </span>
        <span className="memo-foot-spacer" />
        <span className="memo-count">{tMemo('memo.char_count', { n: draft.body.length })}</span>
      </div>
    </>
  );
}
